/**
 * Chrome for the editor's floating workspace. `FloatPanel` is a draggable,
 * resizable card over the map (drag the header to move it anywhere, resize
 * from the right/bottom/left edges or the corner, collapse to a title bar).
 * `DockDrawer` is the bottom timeline drawer (resize from its top edge,
 * collapse to a bar). Pure chrome: geometry and behaviour arrive via props
 * from useEditorLayout.
 */
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import type { Rect } from './layout';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';
import { PortalIcons } from '../portalIcons';

export type ResizeEdge = 'e' | 'w' | 's' | 'se';

export type FloatShell = {
  rect: Rect;
  collapsed: boolean;
  /** Pinned as a full-height column on `side`, pushing the map. */
  docked: boolean;
  side: 'left' | 'right';
  zIndex: number;
  onMoveStart: (e: ReactPointerEvent) => void;
  onResizeStart: (e: ReactPointerEvent, edge: ResizeEdge) => void;
  onToggleCollapse: () => void;
  onToggleDock: () => void;
  /** Bring this card above its sibling. */
  onFocus: () => void;
};

export type DrawerShell = {
  h: number;
  collapsed: boolean;
  /** Pinned to the bottom edge at full width, pushing the map up. */
  docked: boolean;
  onResizeStart: (e: ReactPointerEvent) => void;
  onToggleCollapse: () => void;
  onToggleDock: () => void;
};

type FloatPanelProps = {
  shell: FloatShell;
  title?: ReactNode;
  sub?: ReactNode;
  headExtra?: ReactNode;
  children: ReactNode;
  /** Card height hugs the content; the shell rect's h becomes a max height. */
  fitContent?: boolean;
};

/** Dock / overlay toggle showing the mode it switches TO. */
function ModeButton({ docked, dockAs, onClick }: { docked: boolean; dockAs: 'side' | 'bottom'; onClick: () => void }) {
  return (
    <button className="oe-collapse oe-mode" title={docked ? 'Overlay' : 'Dock'} onClick={onClick}>
      <Icon of={docked ? PortalIcons.action.overlayPanel : dockAs === 'bottom' ? PortalIcons.action.dockBottom : PortalIcons.action.dockPanel} />
    </button>
  );
}

const collapseIcon = (collapsed: boolean) => <Icon of={collapsed ? PortalIcons.action.expand : PortalIcons.action.collapse} />;

export function FloatPanel({ shell, title, sub, headExtra, children, fitContent }: FloatPanelProps) {
  const { rect, collapsed, docked, side, zIndex, onMoveStart, onResizeStart, onToggleCollapse, onToggleDock, onFocus } = shell;
  // Docked: a full-height column on its edge (width from the rect, the rest
  // is CSS); collapsed it shrinks to a title bar in that corner.
  const style = docked
    ? { [side]: 0, top: 0, width: rect.w, zIndex, ...(collapsed ? {} : { bottom: 'var(--dock-b)' }) }
    // fitContent: the card hugs its content and rect.h acts as a MAX height
    // (short lists get a short card; the body scrolls past the cap).
    : {
        left: rect.x, top: rect.y, width: rect.w, zIndex,
        ...(collapsed ? {} : fitContent ? { maxHeight: rect.h } : { height: rect.h }),
      };
  const innerEdge: ResizeEdge = side === 'left' ? 'e' : 'w';
  return (
    <div className={'oe-float' + (docked ? ' docked' : '')} style={style} onPointerDown={onFocus}>
      <div
        className={'oe-panel-head' + (docked ? '' : ' oe-grab')}
        onPointerDown={onMoveStart}
        title={docked ? undefined : 'Drag to move this panel'}
      >
        {!docked && <span className="oe-grip" aria-hidden><Icon of={Icons.action.dragHandle} size={12} /></span>}
        {title != null && <h3>{title}</h3>}
        {sub != null && <span className="oe-sub">{sub}</span>}
        <span className="oe-spacer" />
        {headExtra}
        <ModeButton docked={docked} dockAs="side" onClick={onToggleDock} />
        <button className="oe-collapse" title={collapsed ? 'Expand' : 'Collapse to title bar'} onClick={onToggleCollapse}>
          {collapseIcon(collapsed)}
        </button>
      </div>
      {!collapsed && children}
      {!collapsed && docked && (
        <div className={'oe-rz ' + innerEdge} title="Drag to resize" onPointerDown={(e) => onResizeStart(e, innerEdge)} />
      )}
      {!collapsed && !docked && (
        <>
          <div className="oe-rz e" title="Drag to resize" onPointerDown={(e) => onResizeStart(e, 'e')} />
          <div className="oe-rz w" title="Drag to resize" onPointerDown={(e) => onResizeStart(e, 'w')} />
          <div className="oe-rz s" title="Drag to resize" onPointerDown={(e) => onResizeStart(e, 's')} />
          <div className="oe-rz se" title="Drag to resize" onPointerDown={(e) => onResizeStart(e, 'se')} />
        </>
      )}
    </div>
  );
}

type DockDrawerProps = {
  shell: DrawerShell;
  title: ReactNode;
  sub?: ReactNode;
  headExtra?: ReactNode;
  children: ReactNode;
};

export function DockDrawer({ shell, title, sub, headExtra, children }: DockDrawerProps) {
  const { h, collapsed, docked, onResizeStart, onToggleCollapse, onToggleDock } = shell;
  return (
    <div className={'oe-drawer' + (docked ? ' docked' : '')} style={{ height: collapsed ? undefined : h }}>
      {!collapsed && <div className="oe-rz n" title="Drag to resize" onPointerDown={onResizeStart} />}
      <div className="oe-panel-head">
        <h3>{title}</h3>
        {sub != null && <span className="oe-sub">{sub}</span>}
        <span className="oe-spacer" />
        {headExtra}
        <ModeButton docked={docked} dockAs="bottom" onClick={onToggleDock} />
        <button className="oe-collapse" title={collapsed ? 'Expand timeline' : 'Collapse timeline'} onClick={onToggleCollapse}>
          {collapseIcon(collapsed)}
        </button>
      </div>
      {!collapsed && children}
    </div>
  );
}
